import { useEffect, useState } from "react";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { BiSolidError } from "react-icons/bi";
import { FaPlus } from "react-icons/fa";
import { FiEdit } from "react-icons/fi";
import { IoCloseSharp } from "react-icons/io5";
import { MdOutlineEdit } from "react-icons/md";
import "react-loading-skeleton/dist/skeleton.css";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useDebounce } from "use-debounce";
import ActionMenu from "../../../component/ActionMenu";
import SkeletonLoading from "../../../component/SkeletonLoading";
import {
  useDeleteRecordMutation,
  useGetAllParentsQuery,
} from "../../../store/api/school/PraentSlice";
import { useCancelParentReservationsMutation } from "../../../store/api/school/ReservationSlice";

import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import AdminPagination from "../../../component/pagination/AdminPagination";
import Tooltip from "../../../component/Tooltip";
import i18n from "../../../i8next";
import { capitalizeString } from "../../../utils/dateAndTimeConversion";

const initialActions = {
  search: "",
};

const initialPagination = {
  limit: 10,
  page: 1,
  total: "",
  totalPages: "",
};

const ParentsList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { slug } = useParams();
  const { user } = useSelector((state) => state.UserReducer);
  const role = user?.role === "super_admin" ? "admin" : user?.role;
  const isAdmin = role === "admin";

  // slug base fetch data
  const userRole =
    slug === "school-parents"
      ? "school"
      : slug === "vendor-parents"
        ? "vendor"
        : null;

  const [show, setShow] = useState(false);
  const [id, setId] = useState("");
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelTarget, setCancelTarget] = useState({ id: null, name: "" });
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounce(search, 300);
  const [parents, setParents] = useState([]);
  const [action, setAction] = useState(initialActions);
  const [pagination, setPagination] = useState(initialPagination);

  const { data, isSuccess, isLoading, isFetching, refetch } =
    useGetAllParentsQuery({
      page: pagination.page,
      limit: pagination.limit,
      ...action,
      role: userRole,
    });

  // delete query
  const [deleteRecord, { isLoading: isDeleteLoading }] =
    useDeleteRecordMutation();

  const [cancelParentReservations, { isLoading: isCancelLoading }] =
    useCancelParentReservationsMutation();

  useEffect(() => {
    if (isSuccess && data?.data) {
      setParents(data.data.parents);
      setPagination(data?.data?.pagination);
    }
  }, [data, isSuccess]);

  // delete function
  const handleDelte = async () => {
    toast.dismiss();
    try {
      const response = await deleteRecord(`${id}`).unwrap();
      if (response.statusCode === 200) {
        toast.success(response.message);
        setShow(false);
        setId("");
        refetch(); // Refresh the list after deletion
      }
    } catch (err) {
      const mesg = err.data?.message || "Something went wrong";
      toast.error(mesg);
    }
  };

  // search State & Debounce Setup
  useEffect(() => {
    if (debouncedSearch) {
      setPagination(() => ({ limit: 10, page: 1 }));
      setAction((prev) => ({ ...prev, search }));
    }
  }, [debouncedSearch]);

  useEffect(() => {
    if (!search.trim()) {
      // setPagination((prev) => ({ limit: 10, page: 1 }));
      setAction((prev) => ({ ...prev, search: "" }));
    }
  }, [search]);

  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, page }));
  };

  const handleLimitChange = (limit) => {
    setPagination((prev) => ({
      ...prev,
      limit,
      page: 1,
    }));
  };

  const handleResetFilters = () => {
    setAction(initialActions);
    setPagination((prev) => ({
      ...prev,
      limit: 10,
      page: 1,
    }));
    setSearch("");
  };

  const handleClose = () => setShow(false);

  const handleCancelParentReservations = async () => {
    toast.dismiss();
    try {
      const response = await cancelParentReservations(cancelTarget.id).unwrap();
      toast.success(response.message || "Reservations cancelled successfully");
      setShowCancelConfirm(false);
      setCancelTarget({ id: null, name: "" });
    } catch (err) {
      toast.error(err.data?.message || "Something went wrong");
    }
  };

  // Language Change Handler
  useEffect(() => {
    const h = () => {
      refetch?.();
    };
    i18n.on("languageChanged", h);
    return () => i18n.off("languageChanged", h);
  }, [refetch]);

  return (
    <>
      <div className="d-flex justify-content-between align-items-center flex-wrap w-100 mb-2">
        <div className="total">
          {t("total")} {t("parents")}: {pagination?.total}
        </div>

        <div className="d-flex flex-wrap gap-2 ms-auto">
          <div className="d-flex flex-grow-1">
            <input
              type="text"
              className="form-control custom-field-one"
              placeholder={`${t("search")}..`}
              value={search}
              onChange={(e) => setSearch(e.target.value.trim())}
            />
          </div>

          <div className="d-flex gap-2 ms-auto">
            <button
              className="btn btn-primary addDriver"
              onClick={handleResetFilters}
            >
              {t("btns.reset")}
            </button>

            {!isAdmin && (
              <NavLink
                to="add"
                className="page-add-btn d-flex align-items-center justify-content-center flex-grow-1 gap-2"
              >
                <FaPlus /> {t("add")} {t("parent")}
              </NavLink>
            )}
          </div>
        </div>
      </div>

      <div className="table m-0">
        <table>
          <thead>
            <tr>
              <th className="text-nowrap">{`${t("Sr")}. ${t("no")}.`}</th>
              <th className="text-nowrap">
                {t("parent")} {t("name")}
              </th>
              <th>{t("email")}</th>
              <th
                className="text-nowrap"
                style={{ minWidth: "400px", width: "auto" }}
              >
                {t("address")}
              </th>
              <th>
                {t("mobile")} {t("no")}.
              </th>
              <th>{t("status")}</th>
              {!isAdmin && <th>{t("action")}</th>}
              {isAdmin && <th>{t("action")}</th>}
            </tr>
          </thead>
          <tbody>
            {isLoading || isFetching ? (
              <SkeletonLoading MainLength={10} length={isAdmin ? 6 : 7} />
            ) : parents?.length > 0 ? (
              parents?.map((item, i) => {
                const link = isAdmin
                  ? `/${slug}/${item.id}/students`
                  : `/parents/${item.id}/students`;

                return (
                  <tr key={i}>
                    <td>{(pagination.page - 1) * pagination.limit + i + 1}</td>
                    <td className="text-nowrap">
                      {/* Make parent name clickable link */}
                      <Tooltip
                        title={`${t("view")} ${item?.full_name || ""}'s ${t("students")} ${t("details")}`}
                      >
                        <NavLink
                          to={link}
                          className="text-decoration-none text-primary fw-medium"
                        >
                          {item?.full_name}
                        </NavLink>
                      </Tooltip>
                    </td>
                    <td className="text-nowrap">{item?.email}</td>
                    <td style={{ minWidth: "400px" }}>{item?.street}</td>
                    <td className="text-nowrap">{item?.phone}</td>
                    <td>
                      <span
                        className={`badge ${
                          item?.status === "payment_pending"
                            ? "bg-warning"
                            : item?.status === "pending"
                              ? "bg-secondary"
                              : "bg-success"
                        }`}
                      >
                        {item?.status ? capitalizeString(item?.status) : "N/A"}
                      </span>
                    </td>
                    {!isAdmin && (
                      <td>
                        <ActionMenu
                          actions={[
                            {
                              label: t("edit"),
                              icon: <MdOutlineEdit size={16} />,
                              onClick: () =>
                                navigate(`/parents/edit/${item.id}`),
                            },
                          ]}
                        />
                      </td>
                    )}
                    {isAdmin && (
                      <td>
                        <ActionMenu
                          actions={[
                            {
                              label: "Cancel Reservations",
                              icon: <IoCloseSharp size={15} />,
                              onClick: () => {
                                setCancelTarget({
                                  id: item.id,
                                  name: item.full_name || item.email,
                                });
                                setShowCancelConfirm(true);
                              },
                              danger: true,
                            },
                          ]}
                        />
                      </td>
                    )}
                  </tr>
                );
              })
            ) : (
              <tr className="text-center">
                <td colSpan="7">
                  <div>
                    <span>
                      {t("no")} {t("parent")} {t("found")}
                    </span>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <AdminPagination
        totalPages={pagination.totalPages}
        currentPage={pagination.page}
        limit={pagination.limit}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
      />

      <Modal show={show}>
        <Button className="closePopup" onClick={handleClose}>
          <IoCloseSharp />
        </Button>
        <div className="popupBox p-3 p-md-5">
          <div>
            <div className="text-center mb-3 errorIcon">
              <BiSolidError />
            </div>
            <div className="text-center mb-4 deleteHd">{t("delete-modal")}</div>
            <div className="text-center">
              <Button className="smallBtn cancel me-3" onClick={handleClose}>
                {t("btns.cancel")}
              </Button>
              <Button className="smallBtn auto" onClick={handleDelte}>
                {isDeleteLoading ? (
                  <div className="spinner-border" role="status">
                    <span className="sr-only p-0"></span>
                  </div>
                ) : (
                  t("delete")
                )}
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Cancel Parent Reservations Confirm Modal */}
      <Modal show={showCancelConfirm} centered>
        <Button
          className="closePopup"
          onClick={() => setShowCancelConfirm(false)}
        >
          <IoCloseSharp />
        </Button>
        <div className="popupBox p-3 p-md-5">
          <div className="text-center mb-3 errorIcon">
            <BiSolidError />
          </div>
          <div className="text-center mb-2 deleteHd">Cancel Reservations</div>
          {cancelTarget.name && (
            <div className="text-center mb-2" style={{ fontSize: "0.9rem" }}>
              <strong>{cancelTarget.name}</strong>
            </div>
          )}
          <div
            className="mb-4 p-2 text-center"
            style={{
              background: "#fff3cd",
              border: "1px solid #ffc107",
              borderRadius: "8px",
              fontSize: "0.82rem",
              color: "#856404",
            }}
          >
            ⚠️ All <strong>active reservations</strong> for this parent and
            their children will be cancelled.
          </div>
          <div className="text-center">
            <Button
              className="smallBtn cancel me-3"
              onClick={() => setShowCancelConfirm(false)}
              disabled={isCancelLoading}
            >
              {t("btns.cancel")}
            </Button>
            <Button
              className="smallBtn auto"
              onClick={handleCancelParentReservations}
              disabled={isCancelLoading}
            >
              {isCancelLoading ? (
                <div className="spinner-border" role="status">
                  <span className="sr-only p-0"></span>
                </div>
              ) : (
                "Yes, Cancel"
              )}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};
export default ParentsList;
